// Party read-flip (WS-A) — TypeScript mirrors of the Party CRUD DTOs.
// Source of truth:
//   src/PhoenixCapital.Core/DTOs/Parties/PartyDtos.cs
//   src/PhoenixCapital.Core/Enums/Parties/PartyEnums.cs
// Update these in lockstep with the backend.

import type {
  AccreditedInvestorBasis,
  AccreditedInvestorStatus,
  AddressDto,
  BankAccountType,
  CounterpartyTaxFilingStatus,
  EntityFormationStatus,
  EntityTypeCode,
  MaritalStatus,
  PreferredContactMethod,
  PreliminaryCreditScoreSource,
  PrimaryPhoneType,
  USCitizenStatus,
} from "@/types/counterparties";

export type PartyType = "Borrower" | "Seller";
export type PartyKind = "Person" | "Business";

export interface PartyListItemDto {
  id: string;
  displayName: string;
  email: string;
  phone: string;
  partyKind: PartyKind;
  city: string;
  state: string;
  portalUserId?: string | null;
  awaitingReviewCount: number;
  overdueCount: number;
  needsAttention: number;
  createdAt: string;
}

export interface PartyContactDto {
  id: string;
  firstName: string;
  lastName: string;
  title?: string | null;
  email?: string | null;
  phone?: string | null;
  isPrimary: boolean;
  notes?: string | null;
}

export interface OwnedEntityDto {
  id: string;
  legalName: string;
  dba?: string | null;
  entityType: EntityTypeCode;
  stateOfFormation?: string | null;
  dateOfFormation?: string | null;
  einMasked?: string | null;
  generalNotes?: string | null;
}

export interface PartyLinkedRecordDto {
  id: string;
  number: string;
  role: "Borrower" | "Seller";
}

export interface PartyDetailDto {
  id: string;
  partyType: PartyType;
  partyKind: PartyKind;
  displayName: string;
  email: string;
  secondaryEmail?: string | null;
  phone: string;
  primaryPhoneType?: PrimaryPhoneType | null;
  secondaryPhone?: string | null;
  preferredContactMethod?: PreferredContactMethod | null;
  firstName?: string | null;
  middleName?: string | null;
  lastName?: string | null;
  suffix?: string | null;
  dateOfBirth?: string | null;
  ssnMasked?: string | null;
  usCitizen?: USCitizenStatus | null;
  maritalStatus?: MaritalStatus | null;
  legalName?: string | null;
  dba?: string | null;
  entityType?: EntityTypeCode | null;
  stateOfFormation?: string | null;
  dateOfFormation?: string | null;
  einMasked?: string | null;
  formationStatus?: EntityFormationStatus | null;
  address: AddressDto;
  mailingAddress?: AddressDto | null;
  industryExperienceYears?: number | null;
  estimatedNetWorth?: number | null;
  estimatedLiquidity?: number | null;
  preliminaryCreditScore?: number | null;
  preliminaryCreditScoreSource?: PreliminaryCreditScoreSource | null;
  equityInjectionSource?: string | null;
  accreditedInvestorStatus?: AccreditedInvestorStatus | null;
  accreditedInvestorBasis?: AccreditedInvestorBasis | null;
  taxFilingStatus?: CounterpartyTaxFilingStatus | null;
  originalSaleDate?: string | null;
  originalSaleBasisInfo?: string | null;
  bankACHName?: string | null;
  bankRoutingNumberMasked?: string | null;
  bankAccountNumberMasked?: string | null;
  bankAccountType?: BankAccountType | null;
  generalNotes?: string | null;
  tags: string[];
  counterpartyId?: string | null;
  portalUserId?: string | null;
  emailOutOfSyncWithAccount: boolean;
  contacts: PartyContactDto[];
  ownedEntities: OwnedEntityDto[];
  linkedDeals: PartyLinkedRecordDto[];
  linkedLoans: PartyLinkedRecordDto[];
  createdAt: string;
  modifiedAt?: string | null;
}

export interface CreatePartyRequest {
  partyKind: PartyKind;
  email: string;
  secondaryEmail?: string | null;
  phone?: string | null;
  primaryPhoneType?: PrimaryPhoneType | null;
  secondaryPhone?: string | null;
  preferredContactMethod?: PreferredContactMethod | null;
  firstName?: string | null;
  middleName?: string | null;
  lastName?: string | null;
  suffix?: string | null;
  dateOfBirth?: string | null;
  ssn?: string | null;
  usCitizen?: USCitizenStatus | null;
  maritalStatus?: MaritalStatus | null;
  legalName?: string | null;
  dba?: string | null;
  entityType?: EntityTypeCode | null;
  stateOfFormation?: string | null;
  dateOfFormation?: string | null;
  ein?: string | null;
  formationStatus?: EntityFormationStatus | null;
  address?: AddressDto | null;
  mailingAddress?: AddressDto | null;
  industryExperienceYears?: number | null;
  estimatedNetWorth?: number | null;
  estimatedLiquidity?: number | null;
  preliminaryCreditScore?: number | null;
  preliminaryCreditScoreSource?: PreliminaryCreditScoreSource | null;
  equityInjectionSource?: string | null;
  accreditedInvestorStatus?: AccreditedInvestorStatus | null;
  accreditedInvestorBasis?: AccreditedInvestorBasis | null;
  taxFilingStatus?: CounterpartyTaxFilingStatus | null;
  originalSaleDate?: string | null;
  originalSaleBasisInfo?: string | null;
  bankACHName?: string | null;
  bankRoutingNumber?: string | null;
  bankAccountNumber?: string | null;
  bankAccountType?: BankAccountType | null;
  generalNotes?: string | null;
  tags?: string[] | null;
}

/** FIND-12: optional strings — null/undefined = unchanged, "" = clear.
 *  Sensitive fields (ssn/ein/bank numbers) are never echoed back; leave
 *  them null to keep the stored ciphertext. Nullable value types are
 *  null = unchanged (the edit page hydrates and re-sends current values). */
export interface UpdatePartyRequest extends Omit<CreatePartyRequest, "partyKind"> {
  clearMailingAddress?: boolean;
}

export interface PartyContactRequest {
  id?: string | null;
  firstName: string;
  lastName: string;
  title?: string | null;
  email?: string | null;
  phone?: string | null;
  isPrimary: boolean;
  notes?: string | null;
}

export interface OwnedEntityRequest {
  id?: string | null;
  legalName: string;
  dba?: string | null;
  entityType: EntityTypeCode;
  stateOfFormation?: string | null;
  dateOfFormation?: string | null;
  /** null = keep current ciphertext, "" = clear, non-blank = re-encrypt. */
  ein?: string | null;
  generalNotes?: string | null;
}

export type PartySortBy = "displayName" | "createdAt" | "needsAttention";

export interface PartyListQuery {
  search?: string;
  sortBy?: PartySortBy;
  sortDescending?: boolean;
  offset?: number;
  limit?: number;
}

/** Permission strings gating the two party surfaces (orchestrator lands
 *  the backend constants; keep these literals in lockstep). */
export const PARTY_PERMISSIONS: Record<
  PartyType,
  { Create: string; Read: string; Update: string; Delete: string }
> = {
  Borrower: {
    Create: "Borrowers.Create",
    Read: "Borrowers.Read",
    Update: "Borrowers.Update",
    Delete: "Borrowers.Delete",
  },
  Seller: {
    Create: "Sellers.Create",
    Read: "Sellers.Read",
    Update: "Sellers.Update",
    Delete: "Sellers.Delete",
  },
};

/** Route base for a party type's staff pages. */
export function partyBasePath(type: PartyType): string {
  return type === "Borrower" ? "/borrowers" : "/sellers";
}

/** The portal account audience a party type provisions/invites. */
export function partyAccountType(type: PartyType): "Buyer" | "Seller" {
  return type === "Borrower" ? "Buyer" : "Seller";
}
